<script setup lang="ts">
import { CreateStockReconciliation } from "@/api/stockReconciliationApi";
import ConfirmActionDialog from "@/components/dialogs/ConfirmActionDialog.vue";
import StockReconciliationFormLayout from "@/components/warehouse/StockReconciliationFormLayout.vue";
import StockReconciliationItemsTable from "@/components/warehouse/StockReconciliationItemsTable.vue";
import { useStockReconciliationForm } from "@/composables/useStockReconciliationForm";
import { canDoctype } from "@/helper/permissionHelper";
import { presentStockReconciliationError } from "@/services/stockReconciliationErrors";
import useToastify from "@/stores/toastify.store";

/** ⚠️ Inline literal — `definePage` meta is hoisted out of component scope. */
definePage({ meta: { permissionKey: "page.warehouse.stock_reconciliations" } });

const router = useRouter();
const { t } = useI18n();
const toast = useToastify();

const loading = ref(false);
const inlineError = ref<{ title: string; message: string } | null>(null);
const clearDialogVisible = ref(false);

const canCreate = computed(() => canDoctype("Stock Reconciliation", "create"));

// Create is always editing — there is no saved document to switch modes against.
const recoForm = useStockReconciliationForm();

const canSave = computed(() => {
  if (!canCreate.value || loading.value)
    return false;

  if (!recoForm.form.postingDate || !recoForm.hasWarehouse())
    return false;

  return recoForm.countedRows.value.length > 0;
});

/**
 * Why saving may still be refused after `canSave` passes.
 *
 * The backend drops rows that match what it already holds and refuses a document
 * where every row matches. Saying so here — before the request — is the
 * difference between "nothing to correct, good" and an error the operator reads
 * as lost work.
 */
const noChangesWarning = computed(() =>
  recoForm.countedRows.value.length > 0 && !recoForm.hasChangedRows(),
);

const uncostedWarning = computed(() => recoForm.unpricedRows.value.length > 0);

async function submit() {
  if (!canSave.value)
    return;

  if (noChangesWarning.value) {
    toast.warning({ text: t("alkokh.stockReconciliation.messages.noChanges") });

    return;
  }

  inlineError.value = null;

  try {
    const created = await CreateStockReconciliation(loading, recoForm.buildPayload());
    if (!created)
      return;

    toast.success({ text: t("alkokh.stockReconciliation.messages.created") });

    // Stay in the workspace: land on the new document rather than bouncing to the
    // list, so the operator can review or post what they just counted.
    router.push(`/warehouse/stock-reconciliations/${created.id}`);
  } catch (error: any) {
    const presentation = presentStockReconciliationError(error, t);
    toast.error({ text: presentation.toastMessage });

    if (presentation.alertTitle && presentation.alertMessage) {
      inlineError.value = {
        title: presentation.alertTitle,
        message: presentation.alertMessage,
      };
    }
  }
}

function clearAll() {
  recoForm.clearRows();
  clearDialogVisible.value = false;
  toast.success({ text: t("alkokh.stockReconciliation.messages.cleared") });
}
</script>

<template>
  <StockReconciliationFormLayout
    :form-state="recoForm"
    mode="create"
    :is-editing="true"
    :loading="loading"
  >
    <template #actions>
      <VBtn
        variant="outlined"
        color="secondary"
        :disabled="loading"
        @click="router.push('/warehouse/stock-reconciliations')"
      >
        {{ t("alkokh.stockReconciliation.actions.back") }}
      </VBtn>

      <VBtn
        color="primary"
        prepend-icon="tabler-device-floppy"
        :disabled="!canSave"
        :loading="loading"
        @click="submit"
      >
        {{ t("alkokh.stockReconciliation.actions.saveDraft") }}
      </VBtn>
    </template>

    <template #alert>
      <!-- The backend's own sentence, verbatim: it names the row that caused the
           refusal, and that detail is the fix. -->
      <VAlert
        v-if="inlineError"
        type="error"
        variant="tonal"
        :title="inlineError.title"
        :text="inlineError.message"
        closable
        @click:close="inlineError = null"
      />
    </template>

    <template #items>
      <StockReconciliationItemsTable
        :rows="recoForm.rows.value"
        :warehouse="recoForm.form.warehouse"
        :snapshot-state="recoForm.snapshotState.value"
        :loading="recoForm.snapshotLoading.value"
        :opening-stock="recoForm.form.purpose === 'Opening Stock'"
        @update:rows="recoForm.updateRows"
        @retry-snapshot="recoForm.refreshSnapshot"
      />
    </template>

    <template #aside>
      <VAlert
        v-if="noChangesWarning"
        type="info"
        variant="tonal"
        density="compact"
        :title="t('alkokh.stockReconciliation.errors.noChangeTitle')"
        :text="t('alkokh.stockReconciliation.messages.noChanges')"
      />

      <VAlert
        v-if="uncostedWarning"
        type="warning"
        variant="tonal"
        density="compact"
        :title="t('alkokh.stockReconciliation.errors.valuationRateTitle')"
        :text="t('alkokh.stockReconciliation.items.uncostedHint')"
      />

      <VBtn
        v-if="recoForm.rows.value.length"
        block
        variant="outlined"
        color="error"
        prepend-icon="tabler-trash"
        :disabled="loading"
        @click="clearDialogVisible = true"
      >
        {{ t("alkokh.stockReconciliation.actions.clear") }}
      </VBtn>
    </template>

    <ConfirmActionDialog
      v-model:is-dialog-visible="clearDialogVisible"
      :title="t('alkokh.stockReconciliation.clearDialog.title')"
      :message="t('alkokh.stockReconciliation.clearDialog.message', { count: recoForm.rows.value.length })"
      :confirm-btn="t('alkokh.stockReconciliation.clearDialog.confirm')"
      confirm-color="error"
      icon="tabler-trash"
      @confirm="clearAll"
    />
  </StockReconciliationFormLayout>
</template>
